<script setup lang="ts">
import { Button, Input, ZhPanel, ZhSystemLabel } from '@zerohuman/ui'
import { computed, ref } from 'vue'
import { api, type GateRow } from '@/api'
import { requireAiAccount } from '@/lib/aiAccountGuard'

/**
 * A run waiting on a person to sign in to a site (os#1498): the run's model met a sign-in page and stopped. The
 * panel names the site and the page the browser is on, and lets the person take the browser, act on it through the
 * same verbs the run uses, and hand it back. Handing it back saves what the sign-in left (never what was typed) for
 * the run's member on that site, and the run carries on from the signed-in page.
 *
 * What a person types is sent once and forgotten: the box is cleared as it goes, and nothing here shows it again.
 */
const props = defineProps<{ runId: string; gate: GateRow }>()
const emit = defineEmits<{ 'handed-back': [] }>()

function payloadOf(gate: GateRow): Record<string, unknown> {
    return gate.payload && typeof gate.payload === 'object' ? (gate.payload as Record<string, unknown>) : {}
}

function hostOf(url: string): string {
    try {
        return new URL(url).hostname
    } catch {
        return ''
    }
}

const page = computed(() => {
    const url = payloadOf(props.gate)['url']
    return typeof url === 'string' ? url : ''
})
const site = computed(() => {
    const named = payloadOf(props.gate)['site']
    return typeof named === 'string' && named !== '' ? named : hostOf(page.value)
})

/** Whether this person has taken the browser. Until then the page offers only that. */
const taken = ref(false)
const busy = ref(false)
const error = ref<string | null>(null)
/** Where the browser is after the person's last action, as the API reports it. */
const here = ref<string | null>(null)
/** The page text the person last asked for, already cleaned of what they typed. */
const read = ref<string | null>(null)
const selector = ref('')
const text = ref('')

/**
 * The run's browser lives with the run, not with the server this page talks to, and the request reaches it by relay
 * (os#1498). When it does not answer in time nothing was refused: the run is still waiting, and trying again is right.
 */
const DID_NOT_ANSWER = 'computer_use_browser_did_not_answer'
const DID_NOT_ANSWER_MESSAGE = 'The run’s browser did not answer in time. The run is still waiting, so try again.'

async function attempt(action: () => Promise<void>, refused: string): Promise<void> {
    busy.value = true
    error.value = null
    try {
        await action()
    } catch (failure) {
        const message = failure instanceof Error ? failure.message : ''
        error.value = message.startsWith(DID_NOT_ANSWER) ? DID_NOT_ANSWER_MESSAGE : refused
    } finally {
        busy.value = false
    }
}

function take(): Promise<void> {
    return attempt(async () => {
        await api.computerUsePause(props.runId)
        taken.value = true
    }, 'Could not take the browser. The run may have ended.')
}

function typeText(): Promise<void> {
    // Taken out of the box before it is sent, so a refusal does not leave it on screen either.
    const typed = text.value
    text.value = ''
    return attempt(async () => {
        const result = await api.computerUseAct(props.runId, { kind: 'type', selector: selector.value, text: typed })
        here.value = result.url
    }, 'Could not type into that field.')
}

function click(): Promise<void> {
    return attempt(async () => {
        const result = await api.computerUseAct(props.runId, { kind: 'click', selector: selector.value })
        here.value = result.url
    }, 'Could not click that element.')
}

function readPage(): Promise<void> {
    return attempt(async () => {
        const result = await api.computerUseAct(props.runId, { kind: 'extract', selector: selector.value || 'body' })
        here.value = result.url
        read.value = result.text ?? ''
    }, 'Could not read the page.')
}

/**
 * #1796: handing the browser back completes the sign-in and lets the run's model carry on, as a gate decision does, so
 * it asks for the AI account first. Taking the browser and acting on it are the person's own and never ask.
 */
async function handBack(): Promise<void> {
    if (!(await requireAiAccount())) {
        return
    }
    return attempt(async () => {
        await api.computerUseHandBack(props.runId)
        taken.value = false
        emit('handed-back')
    }, 'Could not hand the browser back. Only someone who may decide this run’s gates can complete its sign-in.')
}
</script>

<template>
	<ZhPanel data-testid="run-sign-in" class="space-y-3">
		<ZhSystemLabel tone="system">Waiting on a sign-in</ZhSystemLabel>
		<p class="text-sm">
			The run needs a person to sign in to <span class="font-medium">{{ site }}</span>.
		</p>
		<p class="text-muted-foreground text-sm break-all">
			The browser is on <span class="font-mono">{{ here ?? page }}</span>
		</p>
		<p class="text-muted-foreground text-xs">
			What you type is sent to the page only. The run's record and its model never see it.
		</p>
		<p v-if="error" class="text-destructive text-sm" data-testid="run-sign-in-error">{{ error }}</p>
		<Button v-if="!taken" type="button" size="sm" data-testid="run-sign-in-take" :disabled="busy" @click="take">
			Take the browser
		</Button>
		<template v-else>
			<label class="block text-sm">
				<span class="text-muted-foreground">Field or button (CSS selector)</span>
				<Input v-model="selector" data-testid="run-sign-in-selector" class="mt-1" placeholder="#email" />
			</label>
			<label class="block text-sm">
				<span class="text-muted-foreground">Text to type</span>
				<Input
					v-model="text"
					data-testid="run-sign-in-text"
					class="mt-1"
					type="password"
					autocomplete="off"
				/>
			</label>
			<div class="flex flex-wrap gap-2">
				<Button type="button" size="sm" variant="outline" data-testid="run-sign-in-type" :disabled="busy" @click="typeText">
					Type
				</Button>
				<Button type="button" size="sm" variant="outline" data-testid="run-sign-in-click" :disabled="busy" @click="click">
					Click
				</Button>
				<Button type="button" size="sm" variant="outline" data-testid="run-sign-in-read" :disabled="busy" @click="readPage">
					Read the page
				</Button>
				<Button type="button" size="sm" data-testid="run-sign-in-hand-back" :disabled="busy" @click="handBack">
					Hand back
				</Button>
			</div>
			<pre
				v-if="read !== null"
				data-testid="run-sign-in-page-text"
				class="bg-muted max-h-64 overflow-auto rounded-md p-3 text-xs whitespace-pre-wrap"
			>{{ read }}</pre>
		</template>
	</ZhPanel>
</template>
